import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { useState, type ChangeEvent, type FormEvent } from "react";
import {
  commitImport,
  previewImport,
  type ImportPreview,
  type ImportRow,
  type RowVerdict,
} from "./api.js";

/**
 * Importing a cohort from a spreadsheet.
 *
 * Two steps, and the gap between them is the design rather than a formality.
 * Twenty children going into a school's records is not a thing to do blind: a
 * column read wrongly, or a list pasted twice, is only obvious while it is
 * still a table on a screen. So the first button writes nothing at all.
 *
 * Two ways in, because they are the two people have. Selecting the cells and
 * pressing copy gives tab-separated text straight into the box; saving the
 * sheet gives a `.csv`, which is read here in the browser and put in the same
 * box — so the file is something to look at before it is something to commit.
 */

const VERDICT: Record<RowVerdict, { label: string; note: string }> = {
  new: { label: "new", note: "" },
  "already-here": {
    label: "already here",
    note: "Somebody of this name is on the books. Left out unless you say otherwise.",
  },
  repeated: {
    label: "repeated",
    note: "This name appears more than once in the list. Left out unless you say otherwise.",
  },
  unusable: { label: "cannot use", note: "" },
};

export function ImportStudents({ onImported }: { onImported: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  function reset() {
    setText("");
    setPreview(null);
    setChosen(new Set());
    setProblem(null);
    setDone(null);
  }

  async function readFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setProblem(null);
    // Read here rather than uploaded: it goes into the box, so what gets
    // checked is the same thing whichever way it arrived.
    setText(await file.text());
    setPreview(null);
  }

  async function check(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      const result = await previewImport(text);
      setPreview(result);
      // Everything new, ticked. Duplicates left for somebody to decide about.
      setChosen(
        new Set(result.rows.filter((row) => row.verdict === "new").map((row) => row.line)),
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not read that.");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    const students = preview.rows
      .filter((row) => chosen.has(row.line) && row.student !== null)
      .map((row) => row.student as NonNullable<ImportRow["student"]>);

    setBusy(true);
    setProblem(null);
    try {
      const result = await commitImport(students);
      const linked = result.created.filter((row) => row.linkedAccounts.length > 0).length;
      const invitationAvailable = result.created.filter((row) => row.hubAccessStatus === "invitation_available").length;
      setDone(
        `Added ${result.created.length} ${result.created.length === 1 ? "student" : "students"}` +
          (linked > 0
            ? `, ${linked} of whom already had a family account, now attached.`
            : ". No family accounts matched yet.") +
          (invitationAvailable > 0
            ? ` ${invitationAvailable} ${invitationAvailable === 1 ? "student has" : "students have"} an address ready for a Hub invitation. Send it from each student's page.`
            : ""),
      );
      setText("");
      setPreview(null);
      setChosen(new Set());
      onImported();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add them.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="row student-import-entry">
        <button className="quiet" type="button" onClick={() => setOpen(true)}>
          Import from a spreadsheet
        </button>
        {done && <ActionConfirmation variant="success" title="Students imported" description={done} reveal />}
      </div>
    );
  }

  const usable = (preview?.rows ?? []).filter((row) => row.student !== null);

  return (
    <form className="panel student-import-form" onSubmit={check}>
      <h2>Import from a spreadsheet</h2>
      <p className="small muted">
        Copy the cells and paste them below, or choose a saved <code>.csv</code>. The first row has
        to be the headings, and one of them has to be a name — everything else is optional and
        matched by whatever it is called. Nothing is created until you have seen what would be.
      </p>

      {problem && <div className="error small">{problem}</div>}
      {done && <ActionConfirmation variant="success" title="Students imported" description={done} reveal />}

      <textarea
        rows={6}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setPreview(null);
        }}
        placeholder={"Name\tParent email\tPhone\nEllie Small\tkathryn@example.com\t07700 900123"}
        style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: 13 }}
      />

      <div className="row student-import-actions">
        <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void readFile(e)} />
        <button type="submit" disabled={busy || text.trim() === ""}>
          {busy && !preview ? "Reading…" : "Check the list"}
        </button>
        <button
          className="quiet"
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </button>
      </div>

      {preview?.fatal && <div className="error small">{preview.fatal}</div>}

      {preview && preview.fatal === null && (
        <>
          <p className="small muted" style={{ marginTop: 16 }}>
            {/* The mapping is shown because reading a column wrongly is the
                mistake with consequences: an address in the wrong field decides
                who receives a child's lesson notes. */}
            Read as:{" "}
            {Object.entries(preview.mapping)
              .map(([field, heading]) => `${heading} → ${LABELS[field] ?? field}`)
              .join(", ")}
            {preview.ignored.length > 0 && (
              <>
                {" "}
                · Ignored: {preview.ignored.join(", ")}
              </>
            )}
          </p>

          <div
            className="student-import-preview-table"
            role="region"
            aria-label="Import preview"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
              event.currentTarget.scrollBy({ left: event.key === "ArrowRight" ? 48 : -48 });
              event.preventDefault();
            }}
          >
            <table>
              <thead>
                <tr>
                  <th />
                  <th>Line</th>
                  <th>Name</th>
                  <th>Parent email</th>
                  <th>Phone</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.line}>
                    <td>
                      <input
                        type="checkbox"
                        checked={chosen.has(row.line)}
                        // Nothing to create from a row that could not be read.
                        disabled={row.student === null}
                        onChange={(e) => {
                          const next = new Set(chosen);
                          if (e.target.checked) next.add(row.line);
                          else next.delete(row.line);
                          setChosen(next);
                        }}
                      />
                    </td>
                    <td className="muted small">{row.line}</td>
                    <td>{row.student?.fullName ?? "—"}</td>
                    <td className="muted small">{row.student?.parentEmail ?? "—"}</td>
                    <td className="muted small">{row.student?.parentPhone ?? "—"}</td>
                    <td className="small">
                      {row.verdict !== "new" && (
                        <span className={row.verdict === "unusable" ? "tag warn" : "tag"}>
                          {VERDICT[row.verdict].label}
                        </span>
                      )}
                      {row.problems.length > 0 && (
                        <div className="muted small">{row.problems.join(" ")}</div>
                      )}
                      {VERDICT[row.verdict].note && (
                        <div className="muted small">{VERDICT[row.verdict].note}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row student-import-summary">
            <span className="muted small">
              {usable.length} readable, {preview.counts.new} new,{" "}
              {preview.counts["already-here"] + preview.counts.repeated} duplicate,{" "}
              {preview.counts.unusable} unusable. {chosen.size} chosen.
            </span>
            <button type="button" disabled={busy || chosen.size === 0} onClick={() => void commit()}>
              {busy ? "Adding…" : `Add ${chosen.size}`}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

/** How each field reads back to somebody checking the mapping. */
const LABELS: Record<string, string> = {
  fullName: "name",
  parentName: "parent name",
  parentEmail: "parent email",
  studentEmail: "student email",
  parentPhone: "phone",
  instrument: "instrument",
  school: "school",
  formClass: "form",
  isUnder18: "under 18",
};
